import apiClient from './apiClient';

export interface BankAccount {
  accountName: string;
  accountNumber: string;
  bankCode: string;
  bankName: string;
}

export interface Driver {
  id: string;
  code: string;
  name: string;
  email: string;
  phone: string;
}

export interface Payout {
  id: string;
  driver: Driver;
  amount: number;
  currency: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  bankAccount: BankAccount;
  requestedAt: string;
  processedAt?: string;
  completedAt?: string;
  failedAt?: string;
  failedReason?: string;
  paystackTransferCode?: string;
  adminNotes?: string;
  retryCount?: number;
}

export interface PayoutsResponse {
  success: boolean;
  payouts: Payout[];
  pagination: {
    total: number;
    limit: number;
    offset: number;
    hasMore: boolean;
  };
}

export interface PayoutActionResponse {
  success: boolean;
  message: string;
  payout: {
    id: string;
    status: string;
    transferCode?: string;
    completedAt?: string;
    refundedAmount?: number;
    reason?: string;
  };
}

/** Payout settings as saved in the dashboard (unset fields are missing) */
export interface PayoutSettingsValues {
  release_delay_hours?: number;
  minimum_payout?: number;
  instant_payout_enabled?: boolean;
  instant_payout_fee_percentage?: number | null;
}

export interface PayoutSettingsResponse {
  success: boolean;
  /** null until an admin saves the settings */
  data: PayoutSettingsValues | null;
}

export interface PayoutOverviewResponse {
  pendingPayouts: { amount: number; count: number };
  activePayouts: { amount: number; count: number };
  completedToday: { amount: number; count: number };
  totalPaidOut: { amount: number; count: number };
  availableEarnings: { amount: number; drivers: number };
  lockedEarnings: { amount: number; drivers: number };
}

export interface DriverEarningsResponse {
  availableBalance: number;
  pendingEarnings: number;
  pendingWithdrawals: number;
  totalWithdrawn: number;
  totalEarnings: number;
  releaseInfo: {
    nextReleaseAt: string | null;
    nextReleaseAmount: number;
    hoursUntilRelease: number | null;
  };
  recentBookings: Array<{
    bookingNumber: string;
    amount: number;
    status: 'released' | 'locked';
    releaseDate: string;
  }>;
}

export const payoutsService = {
  /**
   * Get list of payouts with optional filtering
   */
  getPayouts: async (params?: {
    status?: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'all';
    limit?: number;
    offset?: number;
  }): Promise<PayoutsResponse> => {
    const response = await apiClient.get('/api/v1/admin/payouts', { params });
    return response.data;
  },

  /**
   * Approve a payout and initiate Paystack transfer
   */
  approvePayout: async (payoutId: string): Promise<PayoutActionResponse> => {
    const response = await apiClient.post(`/api/v1/admin/payouts/${payoutId}/approve`);
    return response.data;
  },

  /**
   * Reject a payout with reason
   */
  rejectPayout: async (payoutId: string, reason: string): Promise<PayoutActionResponse> => {
    const response = await apiClient.post(`/api/v1/admin/payouts/${payoutId}/reject`, {
      reason,
    });
    return response.data;
  },

  /**
   * Get payout settings
   */
  getPayoutSettings: async (): Promise<PayoutSettingsResponse> => {
    const response = await apiClient.get('/api/v1/admin/settings/payout');
    return response.data;
  },

  /**
   * Update payout settings (drivers)
   */
  updatePayoutSettings: async (settings: PayoutSettingsValues): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.put('/api/v1/admin/settings/payout', settings);
    return response.data;
  },

  /**
   * Get host payout settings
   */
  getHostPayoutSettings: async (): Promise<PayoutSettingsResponse> => {
    const response = await apiClient.get('/api/v1/admin/settings/host-payout');
    return response.data;
  },

  /**
   * Update host payout settings
   */
  updateHostPayoutSettings: async (settings: PayoutSettingsValues): Promise<{ success: boolean; message: string }> => {
    const response = await apiClient.put('/api/v1/admin/settings/host-payout', settings);
    return response.data;
  },

  /**
   * Get payout overview/dashboard metrics
   */
  getPayoutOverview: async (): Promise<PayoutOverviewResponse> => {
    const response = await apiClient.get('/api/v1/admin/payouts/overview');
    return response.data;
  },

  /**
   * Get driver earnings details
   */
  getDriverEarnings: async (driverId: string): Promise<DriverEarningsResponse> => {
    const response = await apiClient.get(`/api/v1/admin/drivers/${driverId}/earnings`);
    return response.data;
  },

  /**
   * Retry a failed payout
   */
  retryPayout: async (payoutId: string, adminNotes?: string): Promise<PayoutActionResponse> => {
    const response = await apiClient.post(`/api/v1/admin/payouts/${payoutId}/retry`, {
      adminNotes,
    });
    return response.data;
  },
};
